import React, { useEffect, useState } from "react";
import { Amplify, API } from "aws-amplify";
import AWSConfig from "./aws-exports";
import {
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Paper,
} from "@mui/material";
import toast from "react-hot-toast";
import "./App.css";
import useAuth from "./hooks/useAuth";

const Reports = () => {
  const { isAdmin } = useAuth();
  const [months, setMonths] = useState([]);
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [reportType, setReportType] = useState("summary");

  const handleStartMonthSelect = (event) => {
    const selectedMonth = event.target.value;
    setStartMonth(selectedMonth);
    if (reportType !== "summary") {
      setEndMonth(selectedMonth);
    }
  };

  const handleEndMonthSelect = (event) => {
    setEndMonth(event.target.value);
  };

  const handleReportTypeToggle = (event, newReportType) => {
    if (newReportType) {
      setReportType(newReportType);
      if (newReportType === "dbr") {
        setEndMonth(startMonth);
      }
    }
  };

  const getAvailableReports = (event) => {
    API.get("reports", "reports", {})
      .then((response) => {
        setMonths(response.months);
        const now = new Date();
        const previousDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const year = previousDate.getFullYear();
        const month = (previousDate.getMonth() + 1).toString().padStart(2, "0");
        const formattedDate = `${year}-${month}`;
        setStartMonth(formattedDate);
        setEndMonth(formattedDate);
      })
      .catch((error) => {
        console.log(error.response);
      });
  };

  const handleDownloadReport = (event) => {
    // For dbr, we use startMonth as both start and end
    const effectiveEndMonth = reportType === "dbr" ? startMonth : endMonth;
    console.log(
      `downloading ${reportType} report for ${
        reportType === "dbr"
          ? `month ${startMonth}`
          : `range ${startMonth} to ${endMonth}`
      }`
    );
    API.get("reports", "reports", {
      queryStringParameters: {
        startMonth: startMonth,
        endMonth: effectiveEndMonth,
        type: reportType,
      },
    })
      .then((response) => {
        console.log(response);
        window.open(response.presigned_url, "noreferrer");
      })
      .catch((error) => {
        toast.error(
          `${error.response.data.error}: ${error.response.data.message}`
        );
      });
  };

  useEffect(() => {
    Amplify.configure(AWSConfig);
    getAvailableReports();
  }, []);

  return (
    <>
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4, px: 2 }}>
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 4,
            backgroundColor: "background.paper", 
            color: "text.primary", 
          }}
        >
          <Typography variant="h4" gutterBottom>
            Reports
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This page allows you to download official billing report CSV files
            for your projects.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            The reports are generated monthly and stored securely in an S3
            bucket. You can choose between different report types and select the
            month(s) you need.
          </Typography>
          <Typography variant="body1" sx={{ mb: 0 }}>
            <strong>Summary</strong> reports cover a date range, while{" "}
            <strong>DBR (Detailed Billing Reports)</strong> are for a single month only.
          </Typography>
        </Paper>
        <Box
          display="flex"
          flexWrap="wrap"
          flexGrow={1}
          alignItems="center"
          justifyContent="center"
          columnGap={2}
        >
          <FormControl sx={{ minWidth: 120, color: "black" }}>
            <InputLabel>
              {reportType !== "summary" ? "Month" : "Start Month"}
            </InputLabel>
            <Select
              id="start-month"
              value={startMonth}
              onChange={handleStartMonthSelect}
              label={reportType === "dbr" ? "Month" : "Start Month"}
            >
              {months.map((month, index) => (
                <MenuItem
                  onChange={handleStartMonthSelect}
                  key={index}
                  value={month}
                >
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {reportType === "summary" && (
            <FormControl sx={{ minWidth: 120, color: "black" }}>
              <InputLabel>End Month</InputLabel>
              <Select
                id="end-month"
                value={endMonth}
                onChange={handleEndMonthSelect}
                label="End Month"
              >
                {months.map((month, index) => (
                  <MenuItem
                    onChange={handleEndMonthSelect}
                    key={index}
                    value={month}
                  >
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <ToggleButtonGroup
            sx={{
              m: 2,
              minWidth: 120,
            }}
            color="primary"
            value={reportType}
            exclusive
            onChange={handleReportTypeToggle}
          >
            <ToggleButton
              sx={{
                color: "primary.main",
                backgroundColor: "primary",
                borderColor: "primary.main",
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              }}
              value="summary"
            >
              Summary
            </ToggleButton>
            <ToggleButton
              sx={{
                color: "primary.main",
                backgroundColor: "primary",
                borderColor: "primary.main",
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              }}
              value="dbr"
            >
              DBR
            </ToggleButton>
            {isAdmin && (
              <ToggleButton
                sx={{
                  color: "primary.main",
                  backgroundColor: "primary",
                  borderColor: "primary.main",
                  "&.Mui-selected": {
                    color: "white",
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                }}
                value="anomaly"
              >
                Anomaly
              </ToggleButton>
            )}
          </ToggleButtonGroup>
          <FormControl>
            <Button
              variant="contained"
              margin="dense"
              onClick={handleDownloadReport}
              disabled={months.indexOf(endMonth) > months.indexOf(startMonth)}
            >
              Download Report
            </Button>
          </FormControl>
          {}
        </Box>
      </Box>
    </>
  );
};

export default Reports;
